import {Input, Stack} from '@mantine/core'
import {useMapsLibrary} from '@vis.gl/react-google-maps'
import {useEffect, useRef} from 'react'

import {
    GOOGLE_MAPS_CANCELLED,
    GOOGLE_MAPS_READY,
    type GoogleMapsOperation,
    type GoogleMapsSource,
    startGoogleMapsOperation,
} from './google-maps-metrics'
import classes from './places-autocomplete-input.module.css'

type PlacesAutocompleteInputProps = {
    source: GoogleMapsSource
    label?: string
    placeholder?: string
    error?: string
    onChange: (value: string) => void
    onPlaceSelect: (place: {latitude: number; longitude: number; address: string}) => void
    onLookupError?: (message: string) => void
}

export function PlacesAutocompleteInput({
    source,
    label,
    placeholder,
    error,
    onChange,
    onPlaceSelect,
    onLookupError,
}: PlacesAutocompleteInputProps) {
    const placesLibrary = useMapsLibrary('places')
    const containerRef = useRef<HTMLDivElement>(null)
    const elementRef = useRef<google.maps.places.PlaceAutocompleteElement | null>(null)
    const sourceRef = useRef(source)
    const pendingLookupsRef = useRef(new Set<GoogleMapsOperation>())
    const onChangeRef = useRef(onChange)
    const onPlaceSelectRef = useRef(onPlaceSelect)
    const onLookupErrorRef = useRef(onLookupError)

    sourceRef.current = source
    onChangeRef.current = onChange
    onPlaceSelectRef.current = onPlaceSelect
    onLookupErrorRef.current = onLookupError

    useEffect(() => {
        const container = containerRef.current
        if (!container || elementRef.current || !placesLibrary) {
            return
        }

        const placeAutocomplete = new placesLibrary.PlaceAutocompleteElement({
            placeholder: placeholder ?? 'Search for a US address',
        })
        placeAutocomplete.style.setProperty('color-scheme', 'light')
        placeAutocomplete.style.setProperty('background-color', 'var(--mantine-color-white)')
        placeAutocomplete.style.setProperty('color', 'var(--mantine-color-text)')
        placeAutocomplete.style.setProperty('border', '1px solid var(--mantine-color-gray-4)')
        placeAutocomplete.style.setProperty('border-radius', 'var(--mantine-radius-default)')
        placeAutocomplete.style.setProperty('width', '100%')

        elementRef.current = placeAutocomplete
        container.append(placeAutocomplete)

        const pendingLookups = pendingLookupsRef.current

        placeAutocomplete.addEventListener('gmp-select', async (event) => {
            let lookup: GoogleMapsOperation | undefined
            try {
                const place = event.placePrediction.toPlace()
                lookup = startGoogleMapsOperation(sourceRef.current, 'place_details')
                pendingLookups.add(lookup)
                await place.fetchFields({fields: ['formattedAddress', 'location']})

                const address = place.formattedAddress ?? ''
                const latitude = place.location?.lat()
                const longitude = place.location?.lng()
                if (latitude == null || longitude == null) {
                    lookup.finish({outcome: 'error', reason: 'missing_coordinates'})
                    onLookupErrorRef.current?.(
                        'Unable to resolve this address. Enter latitude and longitude or try again.',
                    )
                    return
                }

                lookup.finish(GOOGLE_MAPS_READY)
                onChangeRef.current(address)
                onPlaceSelectRef.current({latitude, longitude, address})
            } catch {
                lookup?.finish({outcome: 'error', reason: 'lookup_rejected'})
                onLookupErrorRef.current?.(
                    'Unable to resolve this address. Enter latitude and longitude or try again.',
                )
            } finally {
                if (lookup) {
                    pendingLookups.delete(lookup)
                }
            }
        })

        return () => {
            pendingLookups.forEach((lookup) => lookup.finish(GOOGLE_MAPS_CANCELLED))
            pendingLookups.clear()
            elementRef.current?.remove()
            elementRef.current = null
        }
    }, [placesLibrary, placeholder])

    return (
        <Input.Wrapper label={label} error={error}>
            <Stack gap={4}>
                <div ref={containerRef} className={classes.host} />
            </Stack>
        </Input.Wrapper>
    )
}
